import { useState } from 'react'
import { Loader2, Send, CheckCircle2, AlertTriangle } from 'lucide-react'
import api from '../../services/api'

/**
 * ScreenForm — a form a product put on one of its screens, drawn from the data it sent.
 * onSent(message) is called after a successful send, so the page can show the confirmation.
 *
 *   {key, title, description, submit_label, fields:[{name, label, type, required, placeholder, options}]}
 *
 * type is select, text, textarea or url; anything else is drawn as text. The values go to ProTrack,
 * not to the product: ProTrack sends them on from the server, signed as this person, so the
 * product's key never reaches the browser. This component does not know, and is never told, where
 * the form is posted — it names the screen and the form and ProTrack looks the rest up.
 *
 * The product's own words are shown as they come: what it says after a submission, and what it says
 * when it refuses one, both written for the person reading them.
 */
const INPUT = 'w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--pt-accent-soft,#e0e7ff)] focus:border-[var(--pt-accent,#6366f1)]'

export default function ScreenForm({ productKey, viewKey, form, onSent }) {
  const fields = (Array.isArray(form?.fields) ? form.fields : []).filter(f => f && typeof f.name === 'string' && f.name)
  const [values, setValues] = useState({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(null)

  const set = (name, v) => { setValues(p => ({ ...p, [name]: v })); setDone(null); setError(null) }

  const submit = async (e) => {
    e.preventDefault()
    if (busy) return
    setBusy(true); setError(null); setDone(null)
    try {
      const { data } = await api.post(
        `/platform/products/${productKey}/views/${encodeURIComponent(viewKey)}/forms/${encodeURIComponent(form.key)}`,
        { fields: values })
      const msg = data?.message || 'Sent.'
      setValues({})
      // With a host page, the confirmation is shown THERE, not here: a form that has just done its
      // job is often taken off the screen by the product (nothing left to review), and a message
      // inside it would vanish with it. Without one, it is shown in the form itself.
      if (onSent) onSent(msg); else setDone(msg)
    } catch (err) {
      const d = err.response?.data?.detail
      setError(typeof d === 'string' && d.trim() ? d : 'Could not send that. Please try again.')
    } finally { setBusy(false) }
  }

  const id = (f) => `form-${form.key}-${f.name}`

  return (
    <form onSubmit={submit} className="pt-rise rounded-2xl border border-gray-200 border-t-4 border-t-[var(--pt-accent,#4F46E5)] bg-white p-4 sm:p-5 space-y-3.5 shadow-xs">
      <div>
        {form.title && <h3 className="text-sm font-semibold text-gray-900">{form.title}</h3>}
        {form.description && <p className="text-xs text-gray-500 mt-1 leading-relaxed whitespace-pre-line">{form.description}</p>}
      </div>

      {fields.map(f => {
        const t = f.type === 'select' || f.type === 'textarea' || f.type === 'url' ? f.type : 'text'
        const common = { id: id(f), name: f.name, required: !!f.required, disabled: busy, value: values[f.name] ?? '' }
        return (
          <div key={f.name}>
            <label htmlFor={id(f)} className="block text-xs font-medium text-gray-700 mb-1">
              {f.label || f.name}{f.required && <span className="text-red-500" aria-hidden="true"> *</span>}
            </label>
            {t === 'select' ? (
              <select {...common} onChange={e => set(f.name, e.target.value)} className={INPUT}>
                <option value="">Choose…</option>
                {(Array.isArray(f.options) ? f.options : []).filter(o => o && o.value !== undefined && o.value !== null)
                  .map(o => <option key={String(o.value)} value={String(o.value)}>{o.label || String(o.value)}</option>)}
              </select>
            ) : t === 'textarea' ? (
              <textarea {...common} rows={3} placeholder={f.placeholder || ''} onChange={e => set(f.name, e.target.value)} className={INPUT} />
            ) : (
              <input {...common} type={t === 'url' ? 'url' : 'text'} placeholder={f.placeholder || ''} onChange={e => set(f.name, e.target.value)} className={INPUT} />
            )}
          </div>
        )
      })}

      {error && (
        <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
        </p>
      )}
      {done && (
        <p role="status" className="text-xs text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> <span>{done}</span>
        </p>
      )}

      <button type="submit" disabled={busy}
        className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--pt-accent,#4F46E5)] hover:bg-[var(--pt-accent-dark,#4338CA)] disabled:opacity-60 text-white text-xs font-semibold px-4 py-2">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        {busy ? 'Sending…' : (form.submit_label || 'Send')}
      </button>
    </form>
  )
}
